import { formatPhoneNumber, transformContactDetails } from "@/explore/ffl-detail/utils.mjs";
import Licensee, { DefaultShippingAddress, OptOutOptions, PlaceDetails, SHIPPING_ADDRESS_TYPE, TransferFees, TransferFeesStatus, TransfersAccepted } from "@/model/licensee.mjs";
import logger from "@/utils/logger.mjs";
import { random } from "lodash";
import { formattedValues } from "../contact-info.mjs";
import { ContactDetail, CustomAddress } from "@/explore/types";

export interface FFLGetDealers {
  dealers: FFLGetDealer[];
  meta?: any;
}

export enum DeliveryIndicator {
  commercial,
  residential
}

export interface FFLGetDealer {
  id: string; // 5-74-XXX-XX-XX-12346
  expiry: string; // YYYY_-MM-DD
  licensename: string;
  businessname: string;
  contactphone: string;
  contactstreet: string;
  contactcity: string;
  contactstate: string;
  contactpostalcode: string;
  mailcity : string;
  mailpostalcode : string;
  mailstate : string;
  mailstreet : string;
  mailzipcode : string;
  orderscore: number; // integer
  geolat: string; // string representation of float
  geolng: string; // string representation of float
  license_key: string; // what is this?
  type: string;
  placeDetails: PlaceDetails,
  has_verified_certificate: boolean;
  distance: number; // float
  certificate_file: string
  licenseId: string // 4-31-145-01-3J-06245
  emails?: Array<string>
  additionalPhoneNumbers?: Array<string>
  primaryPhone?: string
  formattedPhoneNumbers?: formattedValues[]
  formattedEmails?: formattedValues[]
  fullLicenseKey?: string | null
  // these need to be implemented
  transferfees?: TransferFees;
  transfersaccepted?: TransfersAccepted;
  totalTransfersCount?: number;
  storeTransfersCount?: number;
  pendingTransfersCount?: number;
  businessType?: string;
  optoutoptions?: string;
  dealer_id?: string;
  // ruchin, let's discuss where this is supposed to come from
  residentialdeliveryindicator: string;
  contactNames: Array<string>;
  websiteUrls: Array<string>;
  fees: {
    nicsFee: number | null,
    nfaFee: number | null,
    pistolFee: number | null,
    rifleFee: number | null,
    shotgunFee: number | null,
    sbrFee: number | null,
    suppressorFee: number | null,
    discountMultipleFirearms: number | null
    receiverFee: number | null
  };
  feesStatus: TransferFeesStatus;
  delivery_indicator: DeliveryIndicator | null;
  licensetype: string;
  isDealerOptOut: boolean;
  contactPhones: string[];
  hasLicenseHistory: boolean;
  licensedetails: {
    positionTitle: string;
    printedName: string;
    printedDate: string;
  } | null;
  industryRelevance: number;
  contact_details: Array<ContactDetail>;
  isPreferred: boolean;
  isRange: boolean;
  isStockingDealer: boolean;
  isBlacklisted:boolean;
  custom_addresses: Array<CustomAddress>;
  defaultShippingAddress: DefaultShippingAddress;
  licenseExpiry?: string;
  is_external: boolean;
  ezCheckDetails?: { historyList: Array<{status: string, created_at: string }> }
}

export default class LicenseeBuilder {
  static buildLicensee(i: FFLGetDealer): Licensee {
    const result: Licensee = {
      id: i.id,
      name: i.businessname,
      licenseName: i.licensename,
      fullLicenseKey: i.fullLicenseKey,
      contact: {
        address: {
          street1: i.contactstreet,
          city: i.contactcity,
          state: i.contactstate,
          zip: i.contactpostalcode
        },
        mailAddress: {
          street: i.mailstreet,
          city: i.mailcity,
          state: i.mailstate,
          zip: (i.mailpostalcode || i.mailzipcode)
        },
        phoneNumber: i.contactphone,
        email: i.emails,
        formattedEmails: i.formattedEmails,
        additionalPhoneNumbers: i.additionalPhoneNumbers || [],
        primaryPhone: i.primaryPhone,
        formattedPhoneNumbers: i.formattedPhoneNumbers
      },
      geo: {
        lat: Number.parseFloat(i.geolat),
        lng: Number.parseFloat(i.geolng),
        distance: i.distance
      },
      placeDetails: i.placeDetails,
      dealer_id: i.dealer_id,
      totalTransfersCount: Number(i.totalTransfersCount),
      storeTransfersCount: Number(i.storeTransfersCount),
      pendingTransfersCount: Number(i.pendingTransfersCount),
      licenseProof: i.certificate_file,
      licenseId: i.licenseId,
      expiry: i.expiry,
      has_verified_certificate: i.has_verified_certificate,
      license_key: i.license_key,
      contactNames: i.contactNames,
      websiteUrls: i.websiteUrls,
      transferFees: i.fees,
      transferFeesStatus: i.feesStatus,
      deliveryIndicator: i.delivery_indicator,
      licenseType: i.licensetype,
      isDealerOptOut: i.isDealerOptOut,
      contactPhones: i.contactPhones,
      hasLicenseHistory: i.hasLicenseHistory,
      licensedetails: i.licensedetails,
      industryRelevance: i.industryRelevance,
      contactDetails: transformContactDetails(i.contact_details) || [],
      isPreferred: i.isPreferred,
      isStockingDealer: i.isStockingDealer,
      isRange: i.isRange,
      isBlacklisted:i.isBlacklisted,
      customAddress: i.custom_addresses || [],
      defaultShippingAddress: i.defaultShippingAddress,
      licenseExpiry: i.licenseExpiry,
      isExternal: i.is_external,
      ezCheckDetails: i?.ezCheckDetails && i?.ezCheckDetails?.historyList && i?.ezCheckDetails?.historyList.length > 0 ? {
       ...i?.ezCheckDetails?.historyList[0]
      } : null
    };
    if (i.placeDetails?.google) {
      result.socialRating = {
        count: i.placeDetails.google.user_rating_count,
        score: i.placeDetails.google.user_rating_average
      };
      logger.debug("Build socialRating as", result.socialRating);
    }

    if (i.transfersaccepted) {
      result.transfersAccepted = {
        ...i.transfersaccepted
      };
    } else {
      result.transfersAccepted = {
        pistol: true,
        rifle: true,
        sbr: false,
        aow: false,
        suppressor: false,
        destructiveDevice: false,
        machinegun: false,
        shotgun: true
      };
    }

    result.businessType = i.type || null;
    result.optOutOptions = i.optoutoptions;

    return result;
  }

  static buildLicenseeList(i: FFLGetDealers) {
    return {
      meta: i.meta,
      dealers: i.dealers.map(dealer => LicenseeBuilder.buildLicensee(dealer))
    };
  }

  static buildLicenseDetails(i: FFLGetDealers) {
    return i.dealers.map(dealer => LicenseeBuilder.buildLicensee(dealer));
  }
}
